/**
 * AuthenticationContext
 *
 * Single-responsibility React Context that owns the entire authentication
 * lifecycle: Azure AD login via MSAL, logout, token persistence, token refresh,
 * session timeout (with inactivity tracking + warning), and verify-on-mount.
 *
 * Persists to localStorage key `auth-storage` (same key used by http-client
 * interceptors) so the JWT is available for every outgoing request.
 */

import React, {
    createContext,
    useContext,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { httpClient } from '@/lib/http-client'
import { loginScopes } from '@/lib/msal-config'
import type { User, SessionInfo } from '@/features/auth/types'

// =============================================================================
// Types
// =============================================================================

interface AuthenticationState {
    user: User | null
    accessToken: string | null
    refreshToken: string | null
    isAuthenticated: boolean
    isLoading: boolean
    sessionInfo: SessionInfo | null
}

interface AuthenticationActions {
    login: () => Promise<void>
    mockLogin: (token?: string) => Promise<void>
    setup: () => Promise<void>
    mockSetup: (token?: string) => Promise<void>
    logout: () => Promise<void>
    refreshAccessToken: () => Promise<boolean>
    updateProfile: (data: Partial<User>) => Promise<void>
}

type AuthenticationContextValue = AuthenticationState & AuthenticationActions

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'auth-storage'
const SESSION_CHECK_INTERVAL_MS = 30_000 // 30 seconds
const WARNING_THRESHOLD_MINUTES = 5
const ACTIVITY_EVENTS: string[] = [
    'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click',
]

// =============================================================================
// Helpers
// =============================================================================

/** Read persisted auth state from localStorage. */
function readPersistedState(): Partial<AuthenticationState> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        // Zustand persist stores under `state`; we support both shapes.
        const s = parsed.state ?? parsed
        return {
            user: s.user ?? null,
            accessToken: s.accessToken ?? null,
            refreshToken: s.refreshToken ?? null,
            isAuthenticated: !!s.accessToken,
            sessionInfo: s.sessionInfo ?? null,
        }
    } catch {
        return {}
    }
}

/** Persist auth state to localStorage (readable by http-client interceptors). */
function persistState(state: Partial<AuthenticationState>) {
    try {
        // Keep the Zustand-compatible shape so http-client interceptors keep working.
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                state: {
                    user: state.user ?? null,
                    accessToken: state.accessToken ?? null,
                    refreshToken: state.refreshToken ?? null,
                    isAuthenticated: state.isAuthenticated ?? false,
                    sessionInfo: state.sessionInfo ?? null,
                    lastActivity: Date.now(),
                },
            }),
        )
    } catch {
        // storage full / unavailable — ignore silently
    }
}

function clearPersistedState() {
    localStorage.removeItem(STORAGE_KEY)
}

// =============================================================================
// Context
// =============================================================================

const AuthenticationContext = createContext<AuthenticationContextValue | null>(null)

// =============================================================================
// Provider
// =============================================================================

interface AuthenticationProviderProps {
    children: React.ReactNode
}

export function AuthenticationProvider({ children }: AuthenticationProviderProps) {
    const { instance: msalInstance, inProgress } = useMsal()

    // ── Core state ────────────────────────────────────────────────────
    const [user, setUser] = useState<User | null>(null)
    const [accessToken, setAccessToken] = useState<string | null>(null)
    const [refreshToken, setRefreshToken] = useState<string | null>(null)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(true) // true until initial verify completes
    const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null)

    // ── Session timeout warning ───────────────────────────────────────
    const [showWarning, setShowWarning] = useState(false)
    const [warningSeconds, setWarningSeconds] = useState(0)

    // ── Timer refs ────────────────────────────────────────────────────
    const sessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const statusCheckRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const warningCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

    // ── Persist helper ────────────────────────────────────────────────
    const persist = useCallback(
        (patch: Partial<AuthenticationState>) => {
            const merged = {
                user: patch.user !== undefined ? patch.user : user,
                accessToken: patch.accessToken !== undefined ? patch.accessToken : accessToken,
                refreshToken: patch.refreshToken !== undefined ? patch.refreshToken : refreshToken,
                isAuthenticated: patch.isAuthenticated !== undefined ? patch.isAuthenticated : isAuthenticated,
                sessionInfo: patch.sessionInfo !== undefined ? patch.sessionInfo : sessionInfo,
            }
            persistState(merged)
        },
        [user, accessToken, refreshToken, isAuthenticated, sessionInfo],
    )

    // ── Clear all timers ──────────────────────────────────────────────
    const clearAllTimers = useCallback(() => {
        if (sessionTimeoutRef.current) { clearTimeout(sessionTimeoutRef.current); sessionTimeoutRef.current = null }
        if (inactivityTimeoutRef.current) { clearTimeout(inactivityTimeoutRef.current); inactivityTimeoutRef.current = null }
        if (statusCheckRef.current) { clearInterval(statusCheckRef.current); statusCheckRef.current = null }
        if (warningCountdownRef.current) { clearInterval(warningCountdownRef.current); warningCountdownRef.current = null }
    }, [])

    // ── Logout ────────────────────────────────────────────────────────
    const logout = useCallback(async () => {
        clearAllTimers()
        setShowWarning(false)
        try {
            if (accessToken) {
                await httpClient.post('/auth/logout')
            }
        } catch {
            // best-effort
        }

        setUser(null)
        setAccessToken(null)
        setRefreshToken(null)
        setIsAuthenticated(false)
        setIsLoading(false)
        setSessionInfo(null)
        clearPersistedState()
        delete httpClient.defaults.headers.common['Authorization']

        // Note: MSAL logoutRedirect is intentionally omitted to allow local-only logout.
        // This keeps the user logged into Microsoft in the browser, providing a 
        // smoother re-authentication experience.
    }, [accessToken, clearAllTimers])

    // ── Start session timeout ─────────────────────────────────────────
    const startSessionTimeout = useCallback(
        (info: SessionInfo) => {
            clearAllTimers()

            // Absolute session timeout
            const sessionMs = info.session_timeout_minutes * 60 * 1000
            sessionTimeoutRef.current = setTimeout(() => {
                console.log('Session timeout reached, logging out…')
                logout()
            }, sessionMs)

            // Inactivity timeout
            const inactivityMs = info.inactivity_timeout_minutes * 60 * 1000
            inactivityTimeoutRef.current = setTimeout(() => {
                console.log('Inactivity timeout reached, logging out…')
                logout()
            }, inactivityMs)
        },
        [clearAllTimers, logout],
    )

    // ── Session status polling ────────────────────────────────────────
    const checkSessionStatus = useCallback(async () => {
        try {
            const res = await httpClient.get('/auth/session-status')
            const { expires_in, status } = res.data
            if (expires_in <= WARNING_THRESHOLD_MINUTES * 60 && status === 'warning') {
                setWarningSeconds(expires_in)
                setShowWarning(true)
            }
            if (status === 'critical' || expires_in <= 0) {
                logout()
            }
        } catch {
            // ignore — network hiccup
        }
    }, [logout])

    // ── Bootstrap session after credentials arrive ────────────────────
    const bootstrapSession = useCallback(
        (token: string, info: SessionInfo) => {
            httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
            startSessionTimeout(info)
            // Start periodic status checks
            statusCheckRef.current = setInterval(checkSessionStatus, SESSION_CHECK_INTERVAL_MS)
        },
        [startSessionTimeout, checkSessionStatus],
    )

    // ── Login via Azure AD (redirect flow) ─────────────────────────────
    const login = useCallback(
        async () => {
            setIsLoading(true)
            // Redirect to Microsoft — page will reload when user returns.
            // handleRedirectPromise (below) picks up the response on mount.
            await msalInstance.loginRedirect({
                ...loginScopes,
                state: 'login',
            })
        },
        [msalInstance],
    )

    // ── Mock login (dev only — no MSAL, sends dummy token to backend) ─
    const mockLogin = useCallback(
        async (token: string = 'admin@dev') => {
            setIsLoading(true)
            try {
                const res = await httpClient.post('/auth/login', {
                    azure_ad_token: token,
                })

                const {
                    user: u,
                    access_token,
                    refresh_token,
                    expires_in,
                    access_token_expires,
                    refresh_token_expires,
                    session_timeout_minutes,
                    inactivity_timeout_minutes,
                } = res.data

                const info: SessionInfo = {
                    expires_in,
                    access_token_expires,
                    refresh_token_expires,
                    session_timeout_minutes,
                    inactivity_timeout_minutes,
                }

                setUser(u)
                setAccessToken(access_token)
                setRefreshToken(refresh_token)
                setIsAuthenticated(true)
                setSessionInfo(info)
                setIsLoading(false)

                persistState({
                    user: u,
                    accessToken: access_token,
                    refreshToken: refresh_token,
                    isAuthenticated: true,
                    sessionInfo: info,
                })

                bootstrapSession(access_token, info)
            } catch (error) {
                setIsLoading(false)
                throw error
            }
        },
        [bootstrapSession],
    )

    // ── Setup (first-launch — redirect flow) ──────────────────────────
    const setup = useCallback(
        async () => {
            setIsLoading(true)
            // Redirect to Microsoft — uses state='setup' so the redirect
            // handler knows to call POST /auth/setup instead of /auth/login.
            await msalInstance.loginRedirect({
                ...loginScopes,
                state: 'setup',
            })
        },
        [msalInstance],
    )

    // ── Mock setup (dev only) ──────────────────────────────────────────
    const mockSetup = useCallback(
        async (token: string = 'admin@dev') => {
            setIsLoading(true)
            try {
                const res = await httpClient.post('/auth/setup', {
                    azure_ad_token: token,
                })

                const {
                    user: u, access_token, refresh_token,
                    expires_in, access_token_expires, refresh_token_expires,
                    session_timeout_minutes, inactivity_timeout_minutes,
                } = res.data

                const info: SessionInfo = {
                    expires_in, access_token_expires, refresh_token_expires,
                    session_timeout_minutes, inactivity_timeout_minutes,
                }

                setUser(u)
                setAccessToken(access_token)
                setRefreshToken(refresh_token)
                setIsAuthenticated(true)
                setSessionInfo(info)
                setIsLoading(false)
                persistState({ user: u, accessToken: access_token, refreshToken: refresh_token, isAuthenticated: true, sessionInfo: info })
                bootstrapSession(access_token, info)
            } catch (error) {
                setIsLoading(false)
                throw error
            }
        },
        [bootstrapSession],
    )

    // ── Refresh access token ──────────────────────────────────────────
    const refreshAccessToken = useCallback(async (): Promise<boolean> => {
        try {
            if (!refreshToken) return false

            const res = await httpClient.post(
                '/auth/refresh',
                {},
                { headers: { Authorization: `Bearer ${refreshToken}` } },
            )
            const { access_token, expires_in, access_token_expires } = res.data

            setAccessToken(access_token)
            setSessionInfo((prev) =>
                prev ? { ...prev, expires_in, access_token_expires } : null,
            )

            const newState = {
                user,
                accessToken: access_token,
                refreshToken,
                isAuthenticated: true,
                sessionInfo: sessionInfo
                    ? { ...sessionInfo, expires_in, access_token_expires }
                    : null,
            }
            persistState(newState)

            httpClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
            if (sessionInfo) {
                startSessionTimeout({
                    ...sessionInfo,
                    expires_in,
                    access_token_expires,
                })
            }
            return true
        } catch {
            console.error('Token refresh failed')
            await logout()
            return false
        }
    }, [refreshToken, user, sessionInfo, startSessionTimeout, logout])

    // ── Update profile ────────────────────────────────────────────────
    const updateProfile = useCallback(
        async (data: Partial<User>) => {
            const res = await httpClient.put('/auth/profile', data)
            const updatedUser = res.data.user
            setUser(updatedUser)
            persist({ user: updatedUser })
        },
        [persist],
    )

    // ── Helper: complete backend auth after receiving an AD token ──────
    const completeBackendAuth = useCallback(
        async (azureAdToken: string, endpoint: '/auth/login' | '/auth/setup') => {
            const res = await httpClient.post(endpoint, {
                azure_ad_token: azureAdToken,
            })

            const {
                user: u,
                access_token,
                refresh_token,
                expires_in,
                access_token_expires,
                refresh_token_expires,
                session_timeout_minutes,
                inactivity_timeout_minutes,
            } = res.data

            const info: SessionInfo = {
                expires_in,
                access_token_expires,
                refresh_token_expires,
                session_timeout_minutes,
                inactivity_timeout_minutes,
            }

            setUser(u)
            setAccessToken(access_token)
            setRefreshToken(refresh_token)
            setIsAuthenticated(true)
            setSessionInfo(info)
            setIsLoading(false)

            persistState({
                user: u,
                accessToken: access_token,
                refreshToken: refresh_token,
                isAuthenticated: true,
                sessionInfo: info,
            })

            bootstrapSession(access_token, info)
        },
        [bootstrapSession],
    )

    // ── Handle redirect response + verify on mount ────────────────────
    useEffect(() => {
        // Wait for MSAL to finish initializing before verifying
        if (inProgress !== InteractionStatus.None) return

        let cancelled = false
        async function handleMountAuth() {
            try {
                // Check if this is a redirect return from Azure AD
                const msalResponse = await msalInstance.handleRedirectPromise()

                if (msalResponse?.accessToken) {
                    // We're returning from a redirect — complete the flow
                    const isSetup = msalResponse.state === 'setup'
                    const endpoint = isSetup ? '/auth/setup' : '/auth/login'
                    await completeBackendAuth(msalResponse.accessToken, endpoint)
                    // Navigate to home after successful auth
                    window.history.replaceState({}, '', '/')
                    return
                }
            } catch (error) {
                console.error('Redirect auth failed:', error)
                setIsLoading(false)
                return
            }

            // No redirect response — try restoring from persisted session
            const persisted = readPersistedState()
            if (!persisted.accessToken) {
                setIsLoading(false)
                return
            }

            httpClient.defaults.headers.common['Authorization'] = `Bearer ${persisted.accessToken}`
            try {
                const res = await httpClient.get('/auth/verify')
                if (cancelled) return
                const { user: u } = res.data

                setUser(u)
                setAccessToken(persisted.accessToken)
                setRefreshToken(persisted.refreshToken ?? null)
                setIsAuthenticated(true)
                setSessionInfo(persisted.sessionInfo ?? null)
                setIsLoading(false)

                if (persisted.sessionInfo) {
                    bootstrapSession(persisted.accessToken, persisted.sessionInfo)
                }
            } catch {
                if (cancelled) return
                // Token invalid — clear everything
                setUser(null)
                setAccessToken(null)
                setRefreshToken(null)
                setIsAuthenticated(false)
                setSessionInfo(null)
                setIsLoading(false)
                clearPersistedState()
                delete httpClient.defaults.headers.common['Authorization']
            }
        }
        handleMountAuth()
        return () => { cancelled = true }
    }, [inProgress, msalInstance, bootstrapSession, completeBackendAuth])

    // ── Activity tracking (reset inactivity timer on user input) ──────
    useEffect(() => {
        if (!isAuthenticated || !sessionInfo) return

        const resetInactivity = () => {
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current)
            }
            const inactivityMs = sessionInfo.inactivity_timeout_minutes * 60 * 1000
            inactivityTimeoutRef.current = setTimeout(() => {
                console.log('Inactivity timeout reached, logging out…')
                logout()
            }, inactivityMs)
        }

        ACTIVITY_EVENTS.forEach((e) => document.addEventListener(e, resetInactivity, true))
        return () => {
            ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, resetInactivity, true))
        }
    }, [isAuthenticated, sessionInfo, logout])

    // ── Cleanup on unmount ────────────────────────────────────────────
    useEffect(() => clearAllTimers, [clearAllTimers])

    // ── Warning countdown ─────────────────────────────────────────────
    useEffect(() => {
        if (!showWarning) {
            if (warningCountdownRef.current) {
                clearInterval(warningCountdownRef.current)
                warningCountdownRef.current = null
            }
            return
        }
        warningCountdownRef.current = setInterval(() => {
            setWarningSeconds((prev) => {
                if (prev <= 1) {
                    logout()
                    return 0
                }
                return prev - 1
            })
        }, 1000)
        return () => {
            if (warningCountdownRef.current) {
                clearInterval(warningCountdownRef.current)
                warningCountdownRef.current = null
            }
        }
    }, [showWarning, logout])

    // ── Warning handlers ──────────────────────────────────────────────
    const handleExtendSession = useCallback(async () => {
        const ok = await refreshAccessToken()
        if (ok) {
            setShowWarning(false)
            setWarningSeconds(0)
        } else {
            await logout()
        }
    }, [refreshAccessToken, logout])

    const handleWarningLogout = useCallback(async () => {
        setShowWarning(false)
        await logout()
    }, [logout])

    // ── Context value ─────────────────────────────────────────────────
    const value: AuthenticationContextValue = {
        user,
        accessToken,
        refreshToken,
        isAuthenticated,
        isLoading,
        sessionInfo,
        login,
        mockLogin,
        setup,
        mockSetup,
        logout,
        refreshAccessToken,
        updateProfile,
    }

    // ── Render ────────────────────────────────────────────────────────
    return (
        <AuthenticationContext.Provider value={value}>
            {children}
            {showWarning && (
                <SessionTimeoutWarningInline
                    remainingSeconds={warningSeconds}
                    onExtendSession={handleExtendSession}
                    onLogout={handleWarningLogout}
                />
            )}
        </AuthenticationContext.Provider>
    )
}

// =============================================================================
// Hook
// =============================================================================

export function useAuthentication(): AuthenticationContextValue {
    const ctx = useContext(AuthenticationContext)
    if (!ctx) {
        throw new Error('useAuthentication must be used within an AuthenticationProvider')
    }
    return ctx
}

// =============================================================================
// Inline session-timeout warning (self-contained, no external deps)
// =============================================================================

function SessionTimeoutWarningInline({
    remainingSeconds,
    onExtendSession,
    onLogout,
}: {
    remainingSeconds: number
    onExtendSession: () => void
    onLogout: () => void
}) {
    const isCritical = remainingSeconds <= 60
    const minutes = Math.floor(remainingSeconds / 60)
    const seconds = remainingSeconds % 60
    const display = `${minutes}:${seconds.toString().padStart(2, '0')}`

    return (
        <div className="fixed inset-0 !mt-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 space-y-4">
                <div className="text-center">
                    <h3 className="text-xl font-semibold">
                        {isCritical ? 'Session Expiring Soon!' : 'Session Timeout Warning'}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {isCritical
                            ? 'Your session will expire in less than a minute.'
                            : 'Your session will expire due to inactivity.'}
                    </p>
                </div>
                <div className="flex items-center justify-center text-lg font-medium">
                    <span className={isCritical ? 'text-red-600' : 'text-orange-600'}>
                        {display}
                    </span>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={onExtendSession}
                        className={`flex-1 px-4 py-2 rounded-md text-white font-medium ${isCritical ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                    >
                        Extend Session
                    </button>
                    <button
                        onClick={onLogout}
                        className="flex-1 px-4 py-2 rounded-md border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                    >
                        Logout Now
                    </button>
                </div>
                <p className="text-sm text-gray-400 text-center">
                    Click anywhere on the page to reset the inactivity timer
                </p>
            </div>
        </div>
    )
}
